import Axios from 'axios';
import { parseAsync } from 'json2csv';
import tokml from 'tokml';
import { Map, List } from 'immutable'
import AppConfig from 'components/App/AppConfig';

/**
 * Object URL Management
 *
 * Object URLs created with URL.createObjectURL() consume memory and should be
 * revoked when no longer needed. This module provides automatic tracking and
 * cleanup of Object URLs to prevent memory leaks.
 *
 * Usage:
 * - Use createManagedObjectURL() instead of URL.createObjectURL()
 * - URLs are automatically tracked and cleaned up on page unload
 * - Manually revoke with revokeManagedObjectURL() when done early
 * - Revoke all at once with revokeAllObjectURLs()
 */

/**
 * Set to track all created Object URLs for automatic cleanup
 * @private
 */
const createdUrls = new Set();

/**
 * Creates an Object URL and tracks it for automatic cleanup
 *
 * This function wraps URL.createObjectURL() and registers the URL for
 * automatic cleanup when the page unloads, preventing memory leaks.
 *
 * @param {Blob|File|MediaSource} object - The object to create a URL for
 * @returns {string} The created Object URL
 *
 * @example
 * const blob = new Blob(['Hello, World!'], { type: 'text/plain' });
 * const url = createManagedObjectURL(blob);
 * // Use the URL...
 * // Automatically cleaned up on page unload, or manually with revokeManagedObjectURL(url)
 */
export function createManagedObjectURL(object) {
	const url = URL.createObjectURL(object);
	createdUrls.add(url);
	return url;
}

/**
 * Revokes a tracked Object URL and removes it from tracking
 *
 * Call this when you're done with an Object URL to free memory immediately
 * instead of waiting for automatic cleanup on page unload.
 *
 * @param {string} url - The Object URL to revoke
 *
 * @example
 * const url = createManagedObjectURL(blob);
 * // ... use the URL
 * revokeManagedObjectURL(url); // Clean up immediately when done
 */
export function revokeManagedObjectURL(url) {
	if (createdUrls.has(url)) {
		URL.revokeObjectURL(url);
		createdUrls.delete(url);
	}
}

/**
 * Revokes all tracked Object URLs
 *
 * Useful for cleanup when navigating away from a page or unmounting
 * a component that created multiple Object URLs.
 *
 * @example
 * // In a React component cleanup
 * useEffect(() => {
 *   return () => {
 *     revokeAllObjectURLs();
 *   };
 * }, []);
 */
export function revokeAllObjectURLs() {
	const count = createdUrls.size;
	if (count > 0) {
		createdUrls.forEach(url => {
			URL.revokeObjectURL(url);
		});
		createdUrls.clear();
	}
}

/**
 * Automatic cleanup on page unload
 * Ensures all tracked Object URLs are revoked when the user navigates away
 */
if (typeof window !== 'undefined') {
	window.addEventListener('beforeunload', () => {
		revokeAllObjectURLs();
	});
}

const isLocalhost = process.env.NODE_ENV === 'development' || process.env.LOCALHOST === 'true';

// OPTION B (Check if window.location.hostname === 'localhost', for browser-based code):
// const isLocalhost = (typeof window !== 'undefined' && window.location.hostname === 'localhost');

/**
 * Performance Monitoring Utilities
 *
 * These functions provide optional performance monitoring during development.
 * Currently disabled but can be enabled by uncommenting the conditional logic.
 */

/**
 * Starts a performance timer with the given label
 *
 * Wraps console.time() with environment checks to only run in development.
 * Currently disabled - uncomment the conditional logic to enable.
 *
 * @param {string} label - The label for the timer
 *
 * @example
 * debugTimeStart('data-processing');
 * // ... expensive operation
 * debugTimeEnd('data-processing'); // Logs: "data-processing: 123.45ms"
 */
export function debugTimeStart(label) {
	return;
	// if (isLocalhost) {
		// console.time(label);
	// }
}

/**
 * Ends a performance timer and logs the elapsed time
 *
 * Wraps console.timeEnd() with environment checks to only run in development.
 * Currently disabled - uncomment the conditional logic to enable.
 *
 * @param {string} label - The label for the timer (must match debugTimeStart call)
 *
 * @example
 * debugTimeStart('api-call');
 * await fetchData();
 * debugTimeEnd('api-call'); // Logs elapsed time
 */
export function debugTimeEnd(label) {
	return;
	// if (isLocalhost) {
		// console.timeEnd(label);
	// }
}


/**
 * File Download Utilities
 *
 * Functions for programmatically triggering file downloads in the browser.
 */

/**
 * Triggers a file download from a given URL
 *
 * Creates a temporary anchor element and clicks it to initiate the download.
 * The anchor is immediately removed after triggering the download.
 *
 * @param {string} fileName - The name to save the file as (including extension)
 * @param {string} url - The URL or data URI of the file to download
 *
 * @example
 * downloadFile('report.pdf', 'https://example.com/report.pdf');
 * downloadFile('data.csv', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
 */
export function downloadFile(fileName, url) {
	const downloadLink = document.createElement('a');
	downloadLink.href = url;
	downloadLink.download = fileName;
	document.body.appendChild(downloadLink); // Required for Firefox
	downloadLink.click();
	document.body.removeChild(downloadLink);
}

/**
 * Downloads JSON data from a URL as a .json file
 *
 * Fetches data from the provided URL, converts it to JSON, and triggers a download.
 *
 * @param {string} exportName - The base name for the downloaded file (without extension)
 * @param {string} url - The URL to fetch JSON data from
 *
 * @example
 * downloadAsJson('user-data', 'https://api.example.com/users');
 * // Downloads as "user-data.json"
 */
export function downloadAsJson(exportName, url) {
	Axios.get(url).then((response) => {
		downloadFile(`${exportName}.json`, `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(response.data))}`);
	});
}

/**
 * Converts GeoJSON to KML format with styled line rendering
 *
 * This internal function converts GeoJSON to KML and applies styling:
 * - No polygon fill (outline only)
 * - Random color for lines
 * - 2px line width
 *
 * @private
 * @param {Object} geojson - The GeoJSON object to convert
 * @returns {string} The styled KML string
 */
function toKmlAndStyle(geojson) {
	let kml = tokml(geojson);
	const randomColor = `${Math.floor(Math.random() * 16777215).toString(16)}`;
	let style = '';
	style = '<Style>';
	style += '<PolyStyle>';
	style += '<fill>';
	style += '0';
	style += '</fill>';
	style += '<outline>';
	style += '1';
	style += '</outline>';
	style += '</PolyStyle>';
	style += '<LineStyle>';
	style += '<color>';
	style += 'ff' + randomColor;
	style += '</color>';
	style += '<width>';
	style += '2';
	style += '</width>';
	style += '</LineStyle>';
	style += '</Style>';

	return kml.replaceAll('</Placemark>', style + '</Placemark>');
}

/**
 * Downloads GeoJSON data from a URL as a styled KML file
 *
 * Fetches GeoJSON from the provided URL, converts it to KML with styling,
 * and triggers a download.
 *
 * @param {string} exportName - The base name for the downloaded file (without extension)
 * @param {string} url - The URL to fetch GeoJSON data from
 *
 * @example
 * downloadAsKml('field-boundaries', 'https://api.example.com/geojson');
 * // Downloads as "field-boundaries.kml" with styling applied
 */
export function downloadAsKml(exportName, url) {
	Axios.get(url).then((response) => {
		downloadFile(`${exportName}.kml`, `data:text/kml;charset=utf-8,${encodeURIComponent(toKmlAndStyle(response.data))}`);
	});
}

/**
 * Downloads text content as a file with a specified extension
 *
 * Generic function to download text content as any file type.
 * Commonly used for XML, TXT, or other text-based formats.
 *
 * @param {string} exportName - The base name for the downloaded file (without extension)
 * @param {string} content - The text content to save
 * @param {string} fileExt - The file extension (e.g., 'xml', 'txt', 'aux')
 *
 * @example
 * downloadAsFileWithExt('metadata', xmlContent, 'xml');
 * // Downloads as "metadata.xml"
 */
export function downloadAsFileWithExt(exportName, tifAuxXmlContent, fileExt) {
	downloadFile(`${exportName}.${fileExt}`, `data:text/${fileExt};charset=utf-8,${encodeURIComponent(tifAuxXmlContent)}`);
}

/**
 * Color Utilities
 *
 * Functions for color conversion and manipulation.
 */

/**
 * Converts a hex color code to RGB values
 *
 * @param {string} hex - The hex color code (with or without '#' prefix)
 * @returns {Object|null} Object with r, g, b properties (0-255), or null if invalid
 *
 * @example
 * hexToRgb('#ff5733'); // Returns: { r: 255, g: 87, b: 51 }
 * hexToRgb('00ff00');  // Returns: { r: 0, g: 255, b: 0 }
 */
export function hexToRgb(hex) {
	var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16)
	} : null;
}

/**
 * CSV Export Utilities
 *
 * Functions for converting data to CSV format and generating download URLs.
 */

/**
 * Guesses the locale-appropriate CSV delimiter
 *
 * Different locales use different separators (comma vs semicolon).
 * This function detects the user's locale separator automatically.
 *
 * @private
 * @returns {string} The detected delimiter character (typically ',' or ';')
 *
 * @example
 * // In US locale: returns ','
 * // In European locales: often returns ';'
 */
function guessLocaleDelimeter() {
	let sep = ',';
	try {
		const list = ['a', 'b'];
		const s = list.toLocaleString();
		const reg = /a(.*)b/; // Regex to find the separator between array elements
		sep = reg.exec(s)[1]
	} catch (e) {
		console.error('Error trying to guess the locale separator', e);
	}
	return sep;
}

/**
 * Converts JavaScript objects/arrays to CSV string format
 *
 * Uses the json2csv library with locale-aware delimiter detection.
 * Automatically sets the appropriate delimiter and quote character for the user's locale.
 *
 * @param {Array|Object} dataToConvert - Data to convert (array of objects or single object)
 * @param {Object} options - Options for json2csv library (see https://www.npmjs.com/package/json2csv)
 * @param {string[]} options.fields - Array of field names to include in CSV
 * @param {string} options.delimiter - Override auto-detected delimiter
 * @returns {Promise<string>} Promise that resolves to CSV string
 *
 * @example
 * const data = [{ name: 'John', age: 30 }, { name: 'Jane', age: 25 }];
 * const csv = await getCSVFromObject(data);
 * // Result: "name,age\nJohn,30\nJane,25"
 *
 * @example
 * // With custom fields
 * const csv = await getCSVFromObject(data, { fields: ['name'] });
 * // Result: "name\nJohn\nJane"
 */
export function getCSVFromObject(dataToConvert, options) {
	// Use locale to set CSV separator and string delimiter
	let delimeter = guessLocaleDelimeter();

	options = options || {};
	options = { ...options, delimiter: delimeter, quote: '"' };

	return parseAsync(dataToConvert, (options ? { ...options } : null));
}

/**
 * Converts data to CSV and creates a managed download URL
 *
 * This function converts data to CSV format and creates a blob URL for downloading.
 * The URL is automatically tracked and will be cleaned up on page unload.
 *
 * IMPORTANT: The returned URL is managed - it will be automatically revoked
 * when the page unloads. If you need to revoke it earlier, use revokeManagedObjectURL().
 *
 * @param {Array|Object} dataToConvert - Data to convert to CSV
 * @param {Object} options - Options for json2csv (see getCSVFromObject)
 * @returns {Promise<string>} Promise that resolves to a managed blob URL
 *
 * @example
 * const data = [{ name: 'John', value: 100 }, { name: 'Jane', value: 200 }];
 * const url = await getCSVDownloadUrl(data);
 * downloadFile('export.csv', url);
 * // URL is automatically cleaned up on page unload
 *
 * @example
 * // With custom fields
 * const url = await getCSVDownloadUrl(data, { fields: ['name', 'value'] });
 * downloadFile('report.csv', url);
 * // Clean up immediately when done:
 * revokeManagedObjectURL(url);
 */
export function getCSVDownloadUrl(dataToConvert, options) {
	return new Promise((resolve, reject) => {
		getCSVFromObject(dataToConvert, options)
			.then((csv) => {
				// Create managed Object URL for automatic cleanup
				const url = createManagedObjectURL(new window.Blob(['', csv]));
				resolve(url);
			})
			.catch((err) => { reject(err); });
	});
}

/**
 * React Component Loading Utilities
 *
 * Functions for loading React components with retry logic.
 */

/**
 * Loads a lazy-loaded React component with automatic retry on failure
 *
 * This function is useful for handling network issues when loading code-split
 * components. It will retry multiple times with a delay between attempts.
 *
 * @param {Function} lazyComponent - Function that returns a dynamic import() promise
 * @param {number} attemptsLeft - Number of retry attempts remaining (default: 4)
 * @param {number} interval - Delay between retries in milliseconds (default: 1500)
 * @returns {Promise} Promise that resolves to the loaded component
 *
 * @example
 * // In your route configuration
 * const Dashboard = lazy(() => componentLoader(
 *   () => import('./components/Dashboard'),
 *   3,  // 3 retry attempts
 *   2000 // 2 second delay between retries
 * ));
 *
 * @example
 * // With default retry settings (4 attempts, 1.5s delay)
 * const Profile = lazy(() => componentLoader(
 *   () => import('./components/Profile')
 * ));
 */
export function componentLoader(lazyComponent, attemptsLeft = 4, interval = 1500) {
	return new Promise((resolve, reject) => {
		lazyComponent()
			.then(resolve)
			.catch((error) => {
				setTimeout(() => {
					if (attemptsLeft === 1) {
						reject(error);
						return;
					}
					componentLoader(lazyComponent, attemptsLeft - 1, interval).then(
						resolve,
						reject,
					);
				}, interval);
			});
	});
}

/**
 * Internationalization (i18n) Utilities
 *
 * Functions for handling multilingual content and field translation.
 */

/**
 * Translates a multilingual field to the requested language
 *
 * This function handles various field formats:
 * - Immutable.js Map objects with language keys
 * - Plain JavaScript objects with language keys
 * - Fallback to default locale or first available value
 *
 * @param {Object|string} field - The field to translate (can be object with language keys or plain string)
 * @param {string} language - The desired language code (e.g., 'en', 'es', 'es-AR')
 * @returns {string} The translated value or fallback
 *
 * @example
 * const field = { en: 'Hello', es: 'Hola', fr: 'Bonjour' };
 * translateField(field, 'es'); // Returns: 'Hola'
 * translateField(field, 'de'); // Returns: 'Hello' (falls back to English)
 *
 * @example
 * // With Immutable.js Map
 * const field = Map({ en: 'Hello', es: 'Hola' });
 * translateField(field, 'es-AR'); // Returns: 'Hola' (normalizes 'es-AR' to 'es')
 *
 * @example
 * // Plain string (no translation needed)
 * translateField('Hello', 'es'); // Returns: 'Hello'
 */
export function translateField(field, language) {

	if (language && language.length > 2) { // in cases like es-AR ( agentinian spanish)
		language = language.substring(0, 2); // then it becomes es
	}
	if (field != null && typeof field === 'object') {
		if (field.get !== undefined) {
			if (field.get(language)) {
				return field.get(language);
			} if (field.get(AppConfig.DEFAULT_LOCALE)) {
				return field.get(AppConfig.DEFAULT_LOCALE);
			}
			return field.get(0);
		}
		if (field[language]) {
			return field[language];
		} if (field.en) {
			return field.en;
		}
		return field[0];
	}
	return field;
}

/**
 * Gets the user-facing label for a band in the specified language
 *
 * Handles both Immutable.js Map objects and plain JavaScript objects.
 * Translates multilingual names if available.
 *
 * @param {Object|string} band - The band object (can be Immutable Map or plain object)
 * @param {string} lang - The language code for translation
 * @returns {string} The translated band label or empty string if not found
 *
 * @example
 * const band = { name: { en: 'Temperature', es: 'Temperatura' } };
 * getBandLabel(band, 'es'); // Returns: 'Temperatura'
 *
 * @example
 * // With Immutable.js Map
 * const band = Map({ name: Map({ en: 'NDVI', es: 'NDVI' }) });
 * getBandLabel(band, 'en'); // Returns: 'NDVI'
 */
export function getBandLabel(band, lang) {
  if (!band) return "";

  if (band.get && typeof band.get === "function") {
    const name = band.get("name");
    if (name && typeof name === "object") {
      return translateField(name, lang);
    }
    return name ?? "";
  }

  if (band.name && typeof band.name === "object") {
    return translateField(band.name, lang);
  }

  return band.name ?? band;
}

/**
 * Gets the technical band name used in Google Earth Engine
 *
 * Extracts the 'value' property which typically contains the GEE band identifier.
 *
 * @param {Object|string} band - The band object (can be Immutable Map or plain object)
 * @returns {string} The GEE band name or empty string if not found
 *
 * @example
 * const band = { value: 'B4', name: { en: 'Red' } };
 * getBandNameInGEE(band); // Returns: 'B4'
 *
 * @example
 * // With Immutable.js Map
 * const band = Map({ value: 'NDVI' });
 * getBandNameInGEE(band); // Returns: 'NDVI'
 */
export function getBandNameInGEE(band) {
  if (!band) return "";

  if (band.get && typeof band.get === "function") {
    return band.get("value");
  }

  return band.value ?? band;
}

/**
 * Data Structure Conversion Utilities
 *
 * Functions for converting between Immutable.js and plain JavaScript data structures.
 */

/**
 * Recursively converts Immutable.js data structures to plain JavaScript
 *
 * This function handles:
 * - Immutable.Map → Plain JavaScript object
 * - Immutable.List → Plain JavaScript array
 * - Nested Immutable structures (recursive conversion)
 * - Primitive values (returned as-is)
 *
 * @param {*} immutableData - The Immutable.js data structure to convert
 * @returns {*} Plain JavaScript equivalent
 *
 * @example
 * const immutableMap = Map({ name: 'John', age: 30, tags: List(['dev', 'admin']) });
 * convertImmutableToJS(immutableMap);
 * // Returns: { name: 'John', age: 30, tags: ['dev', 'admin'] }
 *
 * @example
 * // With nested structures
 * const nested = Map({
 *   user: Map({ name: 'Jane' }),
 *   roles: List([Map({ id: 1, name: 'admin' })])
 * });
 * convertImmutableToJS(nested);
 * // Returns: { user: { name: 'Jane' }, roles: [{ id: 1, name: 'admin' }] }
 */
export function convertImmutableToJS(immutableData) {
	if (Map.isMap(immutableData)) {
		const obj = {};
		immutableData.forEach((value, key) => {
			obj[key] = convertImmutableToJS(value);
		});
		return obj;
	} else if (List.isList(immutableData)) {
		return immutableData.map(convertImmutableToJS).toArray();
	} else {
		return immutableData;
	}
}

/**
 * Advanced Color Manipulation Utilities
 *
 * These functions use logarithmic color blending for more perceptually accurate results.
 * Based on: https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)
 */

/**
 * Blends two RGB(A) colors using logarithmic interpolation
 *
 * Uses logarithmic blending for more perceptually accurate color mixing
 * compared to linear interpolation. Handles both RGB and RGBA formats.
 *
 * @param {number} p - Blend percentage (0 = 100% c0, 1 = 100% c1)
 * @param {string} c0 - First color in rgb() or rgba() format
 * @param {string} c1 - Second color in rgb() or rgba() format
 * @returns {string} Blended color in rgb() or rgba() format
 *
 * @example
 * RGB_Log_Blend(0.5, 'rgb(255, 0, 0)', 'rgb(0, 0, 255)');
 * // Returns: 'rgb(180, 0, 180)' (approximate purple)
 *
 * @example
 * // With alpha channel
 * RGB_Log_Blend(0.3, 'rgba(255, 0, 0, 1)', 'rgba(0, 255, 0, 0.5)');
 * // Returns blended color with interpolated alpha
 */
export function RGB_Log_Blend(p, c0, c1) {
	var i = parseInt, r = Math.round, P = 1 - p, [a, b, c, d] = c0.split(","), [e, f, g, h] = c1.split(","), x = d || h, j = x ? "," + (!d ? h : !h ? d : r((parseFloat(d) * P + parseFloat(h) * p) * 1000) / 1000 + ")") : ")";
	return "rgb" + (x ? "a(" : "(") + r((P * i(a[3] == "a" ? a.slice(5) : a.slice(4)) ** 2 + p * i(e[3] == "a" ? e.slice(5) : e.slice(4)) ** 2) ** 0.5) + "," + r((P * i(b) ** 2 + p * i(f) ** 2) ** 0.5) + "," + r((P * i(c) ** 2 + p * i(g) ** 2) ** 0.5) + j;
}

/**
 * Lightens or darkens an RGB(A) color using logarithmic scaling
 *
 * Uses logarithmic shading for more perceptually accurate results.
 * Positive values lighten, negative values darken.
 *
 * @param {number} p - Shade factor (-1 to 1, negative = darken, positive = lighten)
 * @param {string} c - Color in rgb() or rgba() format
 * @returns {string} Shaded color in rgb() or rgba() format
 *
 * @example
 * RGB_Log_Shade(0.5, 'rgb(100, 100, 100)');
 * // Returns: lighter version of gray
 *
 * @example
 * RGB_Log_Shade(-0.3, 'rgb(200, 100, 50)');
 * // Returns: darker version of the color
 */
export function RGB_Log_Shade(p, c) {
	var i = parseInt, r = Math.round, [a, b, c, d] = c.split(","), P = p < 0, t = P ? 0 : p * 255 ** 2, P = P ? 1 + p : 1 - p;
	return "rgb" + (d ? "a(" : "(") + r((P * i(a[3] == "a" ? a.slice(5) : a.slice(4)) ** 2 + t) ** 0.5) + "," + r((P * i(b) ** 2 + t) ** 0.5) + "," + r((P * i(c) ** 2 + t) ** 0.5) + (d ? "," + d : ")");
}

/**
 * Converts RGB(A) color string to hexadecimal format
 *
 * Converts CSS rgb() or rgba() format to hex color code.
 * Optionally removes alpha channel for compatibility.
 *
 * @param {string} rgba - Color in rgb() or rgba() format
 * @param {boolean} forceRemoveAlpha - If true, removes alpha channel (default: false)
 * @returns {string} Hexadecimal color code with '#' prefix
 *
 * @example
 * RGBAToHexA('rgb(255, 87, 51)');
 * // Returns: '#ff5733'
 *
 * @example
 * RGBAToHexA('rgba(255, 87, 51, 0.5)');
 * // Returns: '#ff573380' (with alpha)
 *
 * @example
 * RGBAToHexA('rgba(255, 87, 51, 0.5)', true);
 * // Returns: '#ff5733' (alpha removed)
 */
export function RGBAToHexA(rgba, forceRemoveAlpha = false) {
	return "#" + rgba.replace(/^rgba?\(|\s+|\)$/g, '') // Get's rgba / rgb string values
		.split(',') // Splits them at ","
		.filter((string, index) => !forceRemoveAlpha || index !== 3) // Filter out alpha if requested
		.map(string => parseFloat(string)) // Converts them to numbers
		.map((number, index) => index === 3 ? Math.round(number * 255) : number) // Converts alpha (0-1) to 255 scale
		.map(number => number.toString(16)) // Converts numbers to hex
		.map(string => string.length === 1 ? "0" + string : string) // Adds 0 when length of one number is 1
		.join("") // Puts the array together to a string
}

/**
 * Generates a random hexadecimal color code
 *
 * Creates a random 6-digit hex color code (without '#' prefix).
 * Useful for generating unique colors for data visualization.
 *
 * @returns {string} Random hex color code (e.g., '3A5F9B')
 *
 * @example
 * generateRandomColor(); // Returns: 'A3F5C2' (random)
 * generateRandomColor(); // Returns: '1B8E4F' (different each time)
 *
 * @example
 * // Use with CSS
 * element.style.backgroundColor = '#' + generateRandomColor();
 */
export function generateRandomColor() {
	return Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0').toUpperCase();
};